import React, { ReactNode, useCallback, useEffect, useRef } from 'react';

interface Props {
  children: ReactNode;
  tableId: string;
  className?: string;
  style?: React.CSSProperties;
  tableRef?: (table: HTMLTableElement | null) => void;
}

const StickyHorizontalScrollTable: React.FC<Props> = ({
  children,
  tableId,
  className = 'table-responsive',
  style = {},
  tableRef: externalTableRef,
}) => {
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const stickyScrollRef = useRef<HTMLDivElement>(null);
  const stickyScrollContentRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  useEffect(() => {
    const updateStickyScrollWidth = () => {
      if (tableRef.current && stickyScrollContentRef.current) {
        stickyScrollContentRef.current.style.width =
          tableRef.current.scrollWidth + 'px';
      }
    };

    // Update width when table is rendered or resized
    updateStickyScrollWidth();
    window.addEventListener('resize', updateStickyScrollWidth);

    return () => {
      window.removeEventListener('resize', updateStickyScrollWidth);
    };
  }, [children]);

  const setTableRef = useCallback(
    (el: HTMLTableElement | null) => {
      tableRef.current = el;
      if (el && stickyScrollContentRef.current) {
        stickyScrollContentRef.current.style.width = el.scrollWidth + 'px';
      }
      externalTableRef?.(el);
    },
    [externalTableRef]
  );

  const handleMainScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (stickyScrollRef.current) {
      stickyScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  const handleStickyScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  return (
    <>
      <div
        ref={mainScrollRef}
        id={`${tableId}-main-scroll`}
        className={className}
        style={{
          ...style,
          overflowX: 'auto',
          overflowY: 'visible',
          // Hide the scrollbar visually but keep scroll functionality
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
        }}
        onScroll={handleMainScroll}
      >
        <style>{`
          #${tableId}-main-scroll::-webkit-scrollbar {
            display: none; /* Chrome, Safari, Opera */
          }
        `}</style>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && child.type === 'table') {
            return React.cloneElement(child as React.ReactElement<any>, {
              ref: setTableRef,
            });
          }
          return child;
        })}
      </div>
      <div
        ref={stickyScrollRef}
        id={`${tableId}-sticky-scroll`}
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          overflowX: 'auto',
          overflowY: 'hidden',
          backgroundColor: 'white',
          // Force scrollbar to always show on macOS
          WebkitOverflowScrolling: 'touch',
          height: '20px', // Make the scrollbar area taller
        }}
        onScroll={handleStickyScroll}
      >
        <style>{`
          #${tableId}-sticky-scroll::-webkit-scrollbar {
            height: 12px; /* Make scrollbar taller */
            -webkit-appearance: none;
          }
          #${tableId}-sticky-scroll::-webkit-scrollbar-track {
            background-color: #f1f1f1;
            border-radius: 10px;
          }
          #${tableId}-sticky-scroll::-webkit-scrollbar-thumb {
            background-color: #888;
            border-radius: 10px;
            border: 2px solid #f1f1f1;
          }
          #${tableId}-sticky-scroll::-webkit-scrollbar-thumb:hover {
            background-color: #555;
          }
        `}</style>
        <div
          ref={stickyScrollContentRef}
          style={{ height: '1px' }}
          id={`${tableId}-sticky-scroll-content`}
        ></div>
      </div>
    </>
  );
};

export default StickyHorizontalScrollTable;
